import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { TmntText } from '../Common';
import { LOADING_MESSAGE, ERROR_MESSAGE } from '../Messages/Messages';
import { Column, Row } from '../Common/Grid';
import MyInfoBlock from '../MyInfo/MyInfoBlock';
import CheckoutInfoBlock from '../CheckoutInfoBlock/CheckoutInfoBlock';
import MarketingPreferencesFormBlock from '../MarketingPreferencesFormBlock/MarketingPreferencesFormBlock';
import PaymentInfo from './PaymentInfo';
import { ACCOUNT_DISPLAY_TEXT_QUERY } from './operations';

function SuccessBanner({ text }) {
  if (!text) return null;
  return (
    <div className="badge profile-completion-success-banner" data-testid="profile-completion-success-banner" data-status="success">
      <TmntText tmnt={text} />
    </div>
  );
}

SuccessBanner.propTypes = {
  text: PropTypes.shape({ key: PropTypes.string, value: PropTypes.string }).isRequired,
};

const ProfileCompletionView = memo(({ hasProfileCompletionEnabled, setShowSuccessBanner }) => (
  <Row gutter="140px">
    <Column md="4" xs="8">
      <MyInfoBlock hasProfileCompletionEnabled={hasProfileCompletionEnabled} setShowSuccessBanner={setShowSuccessBanner} />
      <PaymentInfo hasProfileCompletionEnabled={hasProfileCompletionEnabled} />
    </Column>

    <Column md="4" xs="8">
      <MarketingPreferencesFormBlock hasProfileCompletionEnabled={hasProfileCompletionEnabled} />
    </Column>
  </Row>
));

ProfileCompletionView.propTypes = {
  hasProfileCompletionEnabled: PropTypes.bool.isRequired,
  setShowSuccessBanner: PropTypes.func.isRequired,
};

const DefaultView = memo(() => (
  <>
    <MyInfoBlock />
    <CheckoutInfoBlock />
    <MarketingPreferencesFormBlock />
  </>
));

export default function AccountDisplay() {
  const { data, loading, error } = useQuery(ACCOUNT_DISPLAY_TEXT_QUERY);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);

  if (loading) return LOADING_MESSAGE;
  if (error) return ERROR_MESSAGE;

  const { textFor, hasProfileCompletionEnabled } = data;
  const shouldShowBanner = hasProfileCompletionEnabled && showSuccessBanner;

  return (
    <div className="account-display">
      { shouldShowBanner && <SuccessBanner text={textFor?.profileCompletionSuccessText} /> }
      <span className="screen-reader-text">
        <TmntText tmnt={textFor?.pageTitle} />
      </span>
      {
        hasProfileCompletionEnabled
          ? (
            <ProfileCompletionView
              hasProfileCompletionEnabled={hasProfileCompletionEnabled}
              setShowSuccessBanner={setShowSuccessBanner}
            />
          )
          : <DefaultView />
      }
    </div>
  );
}
